import { makeAutoObservable } from 'mobx';
import { DATA } from './data';
import { getRandomIntegerBetween, uuid } from './helpers';
import {
  AIRPORT_DATA,
  AIRPORT_LIST,
  AIRPORT_SLUGS,
  AIRPORT_UPGRADES,
  AirportSlug,
  AirportUpgradeType,
  DEFAULT_UNLOCKED_AIRPORTS,
  OFFLOAD_GRACE_PERIOD_MS,
  POPULATION_INCREASE_EVENT_DURATION_MS,
} from './data/airportData';
import { AIRPLANE_DATA, AVERAGE_AIRPLANE_SPEED } from './data/airplaneData';
import { POPUP } from './popupManager';
import { MANAGER_DATA } from './data/managerData';
import { LOUNGE_DATA } from './data/loungeData';
import { BASE_AIRPORT_PRICE } from './data/baseData';

export class Airport {
  public passengers: Partial<{ [key in AirportSlug]: number }> = {};
  public upgrades: { [k in AirportUpgradeType]: number } = { arrival_speed: 0, capacity: 0, ticket_price_multiplier: 0 };
  public isPopulationIncreaseEvent: boolean = false;
  public otherSlugs: AirportSlug[];
  public isBought: boolean = false;

  private passengerIncreaseTimeout: number | null = null;
  private populationEventTimeout: number | null = null;

  constructor(
    /** The index in the airport list array, on this depends unlock level */
    public index: number,
    public name: string,
    public slug: AirportSlug,
    public pos: { x: number; y: number },
  ) {
    this.otherSlugs = AIRPORT_SLUGS.filter((s) => s !== this.slug);
    this.isBought = this.unlockLevel < DEFAULT_UNLOCKED_AIRPORTS;

    this.passengerIncreaseTimeout = window.setTimeout(() => {
      this.increasePassenger();
      // delay this until next frame when data initializes all airports
    }, 0);
    makeAutoObservable(this);
  }

  getAirplanes() {
    return DATA.airplanes.filter((airplane) => airplane.airportSlug === this.slug && airplane.status === 'idle');
  }

  getUpgradeValue(type: AirportUpgradeType) {
    return AIRPORT_UPGRADES[type].valueFn(this.upgrades[type]);
  }

  getUpgradePrice(type: AirportUpgradeType) {
    let lvl = this.unlockLevel - DEFAULT_UNLOCKED_AIRPORTS + 1;
    if (lvl < 0) lvl = 0; // the first 3 default unlocked airports should have the same prices
    return Math.round(AIRPORT_UPGRADES[type].priceFn(this.upgrades[type], lvl));
  }

  get loungePrice() {
    if (this.index < DEFAULT_UNLOCKED_AIRPORTS) {
      const percentage = (this.unlockLevel + 1) / DEFAULT_UNLOCKED_AIRPORTS;
      return BASE_AIRPORT_PRICE * percentage * 0.3;
    }
    return this.airportPrice * 0.5;
  }

  get managerPrice() {
    if (this.index < DEFAULT_UNLOCKED_AIRPORTS) {
      const percentage = (this.unlockLevel + 1) / DEFAULT_UNLOCKED_AIRPORTS;
      return BASE_AIRPORT_PRICE * percentage * 0.7;
    }
    return this.airportPrice * 0.75;
  }

  /** 0, 1, 2, 3 ... */
  get unlockLevel() {
    return this.index;
  }

  get airportPrice() {
    return AIRPORT_DATA.priceFn(this.unlockLevel);
  }

  setIsBought(value: boolean) {
    this.isBought = value;
  }

  /** Max distance between 2 airports cannot be greated than 100, as their position is set in absolute CSS position */
  distanceTo(airport: Airport) {
    return Math.sqrt((this.pos.x - airport.pos.x) ** 2 + (this.pos.y - airport.pos.y) ** 2);
  }

  increaseUpgradeLevel(type: AirportUpgradeType) {
    this.upgrades[type]++;
    this.upgrades[type] = Math.min(this.upgrades[type], AIRPORT_UPGRADES[type].maxValue);
  }

  get isAvailable() {
    return this.isBought;
  }

  // to keep things more exciting you can buy only a single airport at a time
  get isAvaialbleForPurchase() {
    if (this.isBought) {
      return true;
    }
    const availableAirports = DATA.airports.filter((a) => a.isAvailable).length;
    return availableAirports === this.unlockLevel;
  }

  private increasePassenger() {
    if (!this.isFull && this.isAvailable && !DATA.isVideoPlaying) {
      let passengersToAdd = this.getUpgradeValue('arrival_speed');
      if (this.isPopulationIncreaseEvent) {
        passengersToAdd = Math.round(passengersToAdd * 2);
      }
      passengersToAdd = Math.min(passengersToAdd, this.getUpgradeValue('capacity') - this.passengerCount);

      const lounge = DATA.lounges.find((l) => l.airportSlug === this.slug);
      const loungeChance = lounge?.getUpgradeValue('chance') || 0;
      const loungeTicketPrice = Math.round(lounge?.getUpgradeValue('ticket_price') || 0);

      for (let i = 0; i < passengersToAdd; i++) {
        const otherAvailableSlugs = this.otherSlugs.filter((slug) => DATA.airportBySlug(slug).isAvailable);
        const randomSlug = otherAvailableSlugs[Math.floor(Math.random() * otherAvailableSlugs.length)];
        this.passengers[randomSlug] = (this.passengers[randomSlug] || 0) + 1;
      }

      if (Math.random() < loungeChance) {
        DATA.addMoney(loungeTicketPrice);
      }
    }

    this.passengerIncreaseTimeout = window.setTimeout(() => {
      this.increasePassenger();
    }, 1000);
  }

  substractPassengers(destinationSlug: AirportSlug, amount: number) {
    if (this.passengers[destinationSlug]) {
      this.passengers[destinationSlug]! -= amount;
    }
  }

  initialFill() {
    const count = Math.floor(this.getUpgradeValue('capacity') * 0.5 * Math.random());
    while (this.passengerCount < count) {
      const randomSlug = this.otherSlugs[Math.floor(Math.random() * this.otherSlugs.length)];
      this.passengers[randomSlug] = (this.passengers[randomSlug] || 0) + 1;
    }
  }

  get passengerCount() {
    return Object.values(this.passengers).reduce((sum, count) => sum + count, 0);
  }

  get isFull() {
    return this.passengerCount >= this.getUpgradeValue('capacity');
  }

  beginPopulationIncreaseEvent() {
    this.isPopulationIncreaseEvent = true;
    this.populationEventTimeout = window.setTimeout(() => {
      this.stopPopulationIncreaseEvent();
    }, POPULATION_INCREASE_EVENT_DURATION_MS);
  }

  stopPopulationIncreaseEvent() {
    this.isPopulationIncreaseEvent = false;
    this.populationEventTimeout = null;
  }

  dispose() {
    if (this.passengerIncreaseTimeout) {
      window.clearTimeout(this.passengerIncreaseTimeout);
    }
    if (this.populationEventTimeout) {
      window.clearTimeout(this.populationEventTimeout);
    }
  }
}
